import { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";

import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import Badge from "@mui/material/Badge";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import Avatar from "@mui/material/Avatar";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";

import { AccountContext } from "context/account";
import MyProjectsContext from "context/my-projects";

import api from "services/api";
import ChatRoom from "pages/messages/chatroom";
import SendQuoteMessage from "pages/ma/project/sendquotemessage";
import CategoryContext from "context/category-context";
import JSZip from "jszip";
import FileSaver from "file-saver";

import ProjectStyles from "pages/de/project/detail/components/ProjectStyles";
import ProjectInfo from "pages/de/project/detail/components/ProjectInfo";
import { RemoveProspectDialog } from "components/UpStyled/project-card/ProjectCard2";
import ProjectStatusTag from "components/UpStyled/ProjectStatusTag";

import Error404 from "pages/error/404";
import DraggableDialog from "components/UpStyled/DraggableDialog";
import { Divider } from "antd";
import ChatIcon from "components/Icon/ChatIcon";
import { Chat as ChatIcon2 } from "@mui/icons-material";

import { backendServer } from "services/request";
import socketIOClient from "socket.io-client";
import { isSafari } from "utils/helper";
import Draggable, { DraggableCore } from "react-draggable";
import { getStyleImages } from "utils/imageHelper";

const PROJECT_NAME_MAX_LENGTH = 60;
const PROJECT_DESC_MAX_LENGTH = 1000;

const ProjectDetail = ({ project, editable, ufavor_id = null, pageLoading = false }) => {
  const [data, setData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [othersFiles, setOthersFiles] = useState(project?.metadata?.othersFiles);
  const [chatroomid, setChatRoomId] = useState({ id: "", bid: "" });
  const [tabIndex, setTabIndex] = useState(0);
  const [owner, setOwner] = useState({});
  const [myFavorite, setMyFavorite] = useState({});
  const [removeProspectVisible, setRemoveProspectVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [unReadCount, setUnReadCount] = useState(0);
  const [shake, setShake] = useState(false);
  const [messageDialPosition, setMessageDialPosition] = useState({
    left: "0%",
    top: "0%",
  });
  const [dragStartPosition, setDragStartPosition] = useState({ left: 0, top: 0 });
  const [dragging, setDragging] = useState(false);

  const user = useContext(AccountContext);
  const NEW_CHAT_MESSAGE_EVENT = "newChatMessage"; // Name of the event
  const { retrieveFavorite, removeMyFavoriteProject, addMyFavoriteProject, addUnFavoriteProject } =
    useContext(MyProjectsContext);

  const projectInfoRef = useRef(null);
  const dialButtonRef = useRef(null);

  useEffect(() => {
    setOthersFiles(project?.metadata?.othersFiles || []);
  }, []);

  useEffect(() => {
    if (dialButtonRef?.current) {
      setMessageDialPosition({
        left: `calc(95% - ${dialButtonRef?.current.offsetWidth}px)`,
        top: `calc(0% + ${dialButtonRef?.current.offsetHeight}px)`,
      });
    }
  }, [dialButtonRef?.current, chatroomid]);

  useEffect(() => {
    if (user.id && project.id) {
      //set the project to read if manufacturer view the detail
      retrieveFavoriteInfo();
      updateProjectReadStatus(user.id);
    }
  }, [user.id, project.id]);

  const updateProjectReadStatus = async (userId) => {
    try {
      const payload = {
        resource_type: "prospect",
        user_id: userId,
        resource_id: project.id,
        action: "viewed",
      };
      const { data } = await api.event.update(payload);
    } catch (err) {
      console.log(err);
    }
  };

  const handleDownloadAll = () => {
    let files = [];
    const products = project?.products || [];

    const zip = new JSZip();
    const promises = []; //用于存储多个promise

    products.forEach((product, index) => {
      const imageFiles = product?.style?.imageFiles || [];

      const techPacksFiles = getStyleImages(product, false, ["techPacksFiles"]); // include online techpack files

      const styleName = product?.title || "style_" + index;

      const styleFolder = zip.folder(styleName);

      //project design files
      for (let i = 0; i < imageFiles.length; i++) {
        const sketchesFolder = styleFolder.folder("design");
        for (let i = 0; i < imageFiles.length; i++) {
          if (imageFiles[i]?.url) {
            const promise = getFile(imageFiles[i].url).then((res) => {
              const fileName = imageFiles[i]?.name || "file_" + i;
              sketchesFolder.file(fileName, res, { binary: true });
            });
            promises.push(promise);
          }
        }
      }
      //project techPack files
      if (techPacksFiles.length > 0) {
        for (let i = 0; i < techPacksFiles.length; i++) {
          const techPacksFolder = styleFolder.folder("techPacks");
          if (techPacksFiles[i]?.url) {
            const promise = getFile(techPacksFiles[i].url).then((res) => {
              const fileName = techPacksFiles[i]?.name || "file_" + i;
              techPacksFolder.file(fileName, res, { binary: true });
            });
            promises.push(promise);
          }
        }
      }
    });
    //project reference files
    const othersFiles = project?.metadata?.othersFiles || [];
    if (othersFiles.length > 0) {
      const referenceFolder = zip.folder("reference");
      for (let i = 0; i < othersFiles.length; i++) {
        if (othersFiles[i]?.url) {
          const promise = getFile(othersFiles[i].url).then((res) => {
            const fileName = othersFiles[i]?.name || "file_" + i;
            referenceFolder.file(fileName, res, { binary: true });
          });
          promises.push(promise);
        }
      }
    }
    if (promises && promises.length > 0) {
      const toastId = toast.loading("Start downing project files...");
      Promise.all(promises)
        .then(() => {
          zip
            .generateAsync({
              type: "blob",
              compression: "DEFLATE", // STORE：默认不压缩 DEFLATE：需要压缩
              compressionOptions: {
                level: 4, // 压缩等级1~9    1压缩速度最快，9最优压缩方式
              },
            })
            .then((res) => {
              toast.success("Download project files started successfully!");
              FileSaver.saveAs(res, project.name ? project.name : "project.zip");
            })
            .catch((err) => {
              toast.dismiss(toastId);
            });

          toast.dismiss(toastId);
        })
        .catch((err) => console.log("download failed!"));
    } else {
      toast.error("No available project files!");
    }
  };

  const getFile = (url) => {
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: "GET",
      })
        .then((response) => response.blob())
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  useEffect(() => {
    setData(project);
    if (project.owner_id) {
      try {
        api.chatroom
          .findChatRoom({ userids: [project.owner_id], regardingid: project.id })
          .then(({ data }) => {
            setChatRoomId({ id: data.id, bid: data.id });
            return data;
          });
        api.users
          .retrieve(project.owner_id)
          .then(({ data }) => setOwner(data?.user || {}))
          .catch((err) => console.log(err));
      } catch (err) {
        toast.error(`findChatRoom failed!`);
        console.log(err);
      }
      refreshChatRooms(project);
    }
  }, [project.id, project.owner_id]);

  useEffect(() => {
    if (project.id) {
      let roomId = user.id;
      const chatSocket = socketIOClient(backendServer, {
        query: { roomId },
      });
      chatSocket.on(NEW_CHAT_MESSAGE_EVENT, (message) => {
        if (message.projectid == project.id) {
          console.log("new message");
          refreshChatRooms(project);
        }
      });
      return () => {
        chatSocket?.disconnect();
      };
    }
  }, [project.id]);

  const refreshChatRooms = async (project) => {
    if (project?.id) {
      api.chatroom
        .listChatRoom("", "", project.id)
        .then(({ data }) => {
          var cnt = 0;
          for (const room of data.chats) {
            console.log("ids:", room?.contactid, project.owner_id);
            if (room?.contactid == project.owner_id) {
              if (room.ts > room.viewts) cnt += 1;
            }
          }
          console.log("count:", cnt);

          if (cnt > 0) {
            setShake(true);
            setTimeout(() => setShake(false), 1000);
          }
          setUnReadCount(cnt);
          return data;
        })
        .catch((err) => {
          toast.error(`listChatRoom failed!`);
          console.log(err);
        });
    }
  };

  const ownerStore = owner?.stores && owner.stores.length > 0 ? owner.stores[0] : {};

  const retrieveFavoriteInfo = async () => {
    try {
      const data = await retrieveFavorite(project.id);
      const favorite = data?.favorites && data.favorites.length > 0 ? data.favorites[0] : {};

      setMyFavorite(favorite);
    } catch (err) {
      console.log(err);
    }
  };

  const removeProspect = async (ufavor_id) => {
    try {
      await removeMyFavoriteProject(user.id, user.account_type, ufavor_id);
      retrieveFavoriteInfo();
      toast.success(`Removed from my Prospects successfully!`);
    } catch (err) {
      toast.error(`Could not remove from my Prospects!`);
    }
  };

  const addProspect = async (projectId) => {
    try {
      await addMyFavoriteProject(user.id, user.account_type, projectId);
      retrieveFavoriteInfo();
      toast.success(`Added to my Prospects successfully!`);
    } catch (err) {
      toast.error(`Could not add to my Prospects!`);
    }
  };

  const addUnfavorite = async (projectId) => {
    try {
      await addUnFavoriteProject(user.id, user.account_type, projectId);
      retrieveFavoriteInfo();
    } catch (err) {
      toast.error(`Could not add to not interested list!`);
    }
  };

  const handleDragButtonStart = (e) => {
    let x = e.clientX;
    let y = e.clientY;
    if (isSafari) {
      x = e.screenX;
      y = e.screenY;
    }
    setDragStartPosition({ top: y, left: x });
  };

  const handleDragButtonEnd = (e) => {
    let x = e.clientX;
    let y = e.clientY;
    if (isSafari) {
      x = e.screenX;
      y = e.screenY;
    }

    const moveX = x - dragStartPosition.left;
    const moveY = y - dragStartPosition.top;

    let left = dialButtonRef.current.offsetLeft + moveX;
    let top = dialButtonRef.current.offsetTop + moveY;
    //check boudary
    left = left < 0 ? 0 : left;
    top = top < 0 ? 0 : top;
    if (left + dialButtonRef.current.offsetWidth > window.innerWidth) {
      left = window.innerWidth - dialButtonRef.current.offsetWidth;
    }

    if (top + dialButtonRef.current.offsetHeight > window.innerHeight) {
      top = window.innerHeight - dialButtonRef.current.offsetHeight;
    }

    let posX = (left / window.innerWidth) * 100; //percentage
    let posY = (top / window.innerHeight) * 100;

    // console.log(e.clientX, e.clientY, dragStartPosition.left, dragStartPosition.top);
    // console.log(e);

    setMessageDialPosition({
      top: posY + "%",
      left: posX + "%",
    });
  };

  const phoneNumber = project?.metadata?.business_phone || ownerStore?.metadata?.business_phone;

  if (pageLoading) {
    return (
      <div sx={{ p: 3 }}>
        <SuiBox display="flex" alignItems="center" gap={3}>
          <PulseLoader />
          <SuiTypography variant="h6" fontWeight="medium">
            Loading...
          </SuiTypography>
        </SuiBox>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      {project.id ? (
        <div>
          <Card sx={{ p: 3, mb: 2, position: "relative" }}>
            <SuiBox
              display="flex"
              justifyContent="space-between"
              sx={{ flex: "flex-wrap" }}
              alignItems="flex-start"
            >
              <SuiBox>
                <SuiBox display="flex" justifyContent="flex-start" alignItems="center" py={0.5}>
                  <SuiBox>
                    <Tooltip title={project.name}>
                      <SuiBox>
                        <SuiTypography
                          variant="h5"
                          fontWeight="medium"
                          textTransform="capitalize"
                          lineHeight="1.2"
                          sx={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {project.name}
                        </SuiTypography>
                      </SuiBox>
                    </Tooltip>
                  </SuiBox>
                  <ProjectStatusTag status={project.status} />
                </SuiBox>
                <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                  <SuiTypography variant="body1" textTransform="capitalize" color="text">
                    Designer:{" "}
                  </SuiTypography>
                  {ownerStore?.name ? (
                    <SuiBox
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      gap={1}
                      mb={0.5}
                    >
                      <SuiBox component={Link} to={`/wall/portfolio/${owner.id}`}>
                        <Tooltip position="top" title={ownerStore?.name}>
                          <SuiAvatar
                            src={ownerStore?.metadata?.avatar}
                            alt="profile-image"
                            variant="circle"
                            size="xs"
                            shadow="sm"
                          />
                        </Tooltip>
                      </SuiBox>
                      <SuiBox component={Link} to={`/wall/portfolio/${owner.id}`}>
                        <SuiTypography
                          variant="button"
                          fontWeight="regular"
                          textTransform="capitalize"
                        >
                          {ownerStore?.name}
                        </SuiTypography>
                      </SuiBox>
                      {/* <SuiBox
                        sx={{ cursor: "pointer" }}
                        onClick={() => setChatVisible(!chatVisible)}
                        display="flex"
                        justifyContent="center"
                        gap={0.5}
                      >
                        {" "}
                        <Badge variant="dot" color="error" invisible={unReadCount ? false : true}>
                          <ChatIcon size={5} color="dark" />
                        </Badge>
                        <SuiBox>
                          <SuiTypography
                            sx={{ "&:hover": { textDecoration: "underline", color: "info" } }}
                            variant="button"
                            fontWeight="regular"
                            textTransform="capitalize"
                          >
                            Message
                          </SuiTypography>{" "}
                        </SuiBox>
                      </SuiBox> */}
                    </SuiBox>
                  ) : (
                    "N/A"
                  )}
                </SuiBox>
                <SuiBox
                  display="flex"
                  sx={{ flexWrap: "wrap" }}
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  gap={1}
                >
                  <SuiTypography variant="button" fontWeight="regular" color="text">
                    Contact:
                  </SuiTypography>
                  <SuiBox
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    sx={{ mr: 2 }}
                  >
                    <SuiBox sx={{ mr: 1 }}>
                      <EmailIcon fontSize="small" />
                    </SuiBox>
                    <SuiTypography variant="button" fontWeight="regular" color="text">
                      {project?.metadata?.email || owner.email}
                    </SuiTypography>
                  </SuiBox>
                  {phoneNumber ? (
                    <SuiBox display="flex" alignItems="flex-start" justifyContent="flex-start">
                      <SuiBox>
                        <PhoneIcon fontSize="small" />
                      </SuiBox>
                      <SuiTypography variant="button" fontWeight="regular" color="text">
                        {phoneNumber}
                      </SuiTypography>
                    </SuiBox>
                  ) : null}
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" alignItems="center" gap={1}>
                {project.status == "requested" ? (
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    {!myFavorite?.id || myFavorite?.rating < 0 ? (
                      <SuiButton
                        size="small"
                        sx={{ px: 2, whiteSpace: "nowrap", textTransform: "capitalize" }}
                        variant="outlined"
                        onClick={() => addProspect(project.id)}
                        color="error"
                      >
                        <span className="flex items-center justify-center gap-1">
                          <FavoriteIcon sx={{ height: 16, width: 16 }} />
                          <div className="xsmall:hidden small:inline">interest</div>
                        </span>
                      </SuiButton>
                    ) : (
                      <SuiButton
                        variant={"outlined"}
                        size="small"
                        color="dark"
                        sx={{ px: 2, whiteSpace: "nowrap", textTransform: "capitalize" }}
                        onClick={() => setRemoveProspectVisible(true)}
                      >
                        <span className="flex items-center justify-center gap-1">
                          <HeartBrokenIcon sx={{ height: 16, width: 16 }} />
                          <div className="xsmall:hidden small:inline">Not interested</div>
                        </span>
                      </SuiButton>
                    )}
                  </SuiBox>
                ) : null}

                <SuiButton
                  size="small"
                  sx={{ px: 2, whiteSpace: "nowrap" }}
                  variant="outlined"
                  onClick={handleDownloadAll}
                  color="dark"
                >
                  <span className="flex items-center justify-center gap-1">
                    <FileDownloadOutlinedIcon sx={{ height: 16, width: 16 }} />
                    <SuiTypography
                      variant="text"
                      textTransform="capitalize"
                      className="xsmall:hidden small:inline"
                    >
                      Download Files
                    </SuiTypography>
                  </span>
                </SuiButton>
              </SuiBox>
            </SuiBox>

            <SuiBox position="relative">
              <Grid container>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  {project?.products && project.products.length > 0 ? (
                    <ProjectStyles project={project} />
                  ) : (
                    <ProjectInfo project={project} ref={projectInfoRef} />
                  )}
                </Grid>
                {/* <Grid item xs={12} sm={12} md={12} lg={6}>
                {chatroomid.id ? (
                  <ChatRoom
                    id={chatroomid.id}
                    regardingid={project.id}
                    setChatRoomId={setChatRoomId}
                  />
                ) : (
                  <SendQuoteMessage
                    selectedProject={project}
                    setChatRoomId={setChatRoomId}
                    chatroomId={chatroomid.bid}
                  />
                )}
              </Grid> */}
              </Grid>
              <SuiBox mt={2}>
                {project?.products && project.products.length > 0 ? (
                  <ProjectInfo project={project} ref={projectInfoRef} />
                ) : null}
              </SuiBox>

              <SuiBox
                style={{
                  position: "absolute",
                  top: messageDialPosition.top,
                  left: messageDialPosition.left,
                }}
              >
                <Draggable
                  onDrag={() => setDragging(true)}
                  onStop={() =>
                    setTimeout(() => {
                      setDragging(false);
                    }, 500)
                  }
                >
                  <div>
                    <SpeedDial
                      ref={dialButtonRef}
                      ariaLabel="open chat room"
                      sx={{
                        borderRadius: 0,
                        animation: shake && !chatVisible ? "shake 0.5s" : "",
                      }}
                      FabProps={{
                        variant: "extended",
                        color: "dark",
                        className: "text-gray-100 hover:text-gray-800",
                      }}
                      icon={
                        <SuiBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color="inherit"
                          onClick={(e) => {
                            e.stopPropagation();
                            dragging ? null : setChatVisible(!chatVisible);
                          }}
                        >
                          {" "}
                          <div className="flex items-center gap-1">
                            <Badge
                              variant="dot"
                              color="error"
                              invisible={unReadCount ? false : true}
                            >
                              <ChatIcon size={7} color="inherit" />
                            </Badge>
                            <div variant="h6" fontWeight="regular" textTransform="capitalize">
                              Message
                            </div>{" "}
                          </div>
                          <Divider type="vertical" sx={{ color: "inherit" }} />
                          <Avatar
                            src={owner?.metadata?.avatar}
                            sx={{ width: 26, height: 26, mr: 0.5 }}
                          />
                          {owner?.name}
                        </SuiBox>
                      }
                      onClose={null}
                      onOpen={null}
                      open={false}
                    />
                  </div>
                </Draggable>
              </SuiBox>
            </SuiBox>
          </Card>
        </div>
      ) : null}
      {project.id && removeProspectVisible ? (
        <RemoveProspectDialog
          project={project}
          userid={user.id}
          option={{ fullWidth: true, maxWidth: "sm" }}
          visible={removeProspectVisible}
          okColor="error"
          okText="Remove"
          handleOk={(data) => {
            setLoading(true);
            addUnfavorite(project.id);

            const delayRedirecteFn = setTimeout(() => {
              toast.success(`This Project has been removed from your list!`);
              setRemoveProspectVisible(false);
              setLoading(false);

              clearTimeout(delayRedirecteFn);
              history.back();
            }, 1500);
          }}
          handleClose={() => setRemoveProspectVisible(false)}
          loading={loading}
        />
      ) : null}

      {chatVisible ? (
        <Card
          sx={{ maxWidth: "500px", maxHeight: "80%", overflow: "auto" }}
          className="fixed top-[18%] right-[5%] z-10"
        >
          <div>
            <SuiBox px={2} py={1} display="flex" justifyContent="space-between" alignItems="center">
              <SuiBox>
                <SuiTypography variant="h5">Chat with designer</SuiTypography>
              </SuiBox>
              <IconButton
                aria-label="close"
                onClick={() => setChatVisible(false)}
                sx={{
                  float: "right",
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </SuiBox>

            <SuiBox sx={{ overflow: "auto" }}>
              {chatroomid.id ? (
                <ChatRoom
                  id={chatroomid.id}
                  regardingid={project.id}
                  setChatRoomId={setChatRoomId}
                />
              ) : (
                <SendQuoteMessage
                  selectedProject={project}
                  setChatRoomId={setChatRoomId}
                  chatroomId={chatroomid.bid}
                />
              )}
            </SuiBox>
          </div>
        </Card>
      ) : null}
    </div>
  );
};

export default ProjectDetail;
